// Fachregeln des CRM an einer Stelle (neuratek/software/crm/aufbau.md, prototyp-stand.md; Prototyp CRM ① bis ④).
// Server und Oberfläche nutzen dieselben Werte.

export const LEISTUNGEN = ['Website', 'Unternehmensanalyse', 'Konzeptentwicklung'] as const;

export const TERMIN_ARTEN = ['Erstgespräch', 'Weiterer Termin – Closer', 'Berater-Erstgespräch', 'Weiterer Termin – Berater'] as const;
export type TerminArt = (typeof TERMIN_ARTEN)[number];
export const istBeraterArt = (a: string) => a === 'Berater-Erstgespräch' || a === 'Weiterer Termin – Berater';

// Standarddauer je Art (Vorschlag Jarvis, unter Admin › Einstellungen änderbar); Auswahl der Dauer
export const DAUER_STD: Record<TerminArt, number> = { 'Erstgespräch': 30, 'Weiterer Termin – Closer': 30, 'Berater-Erstgespräch': 60, 'Weiterer Termin – Berater': 60 };
export const DAUERN = [15, 30, 45, 60, 90] as const;
// Freie Zeiten (bis der Google-Kalender verbunden ist: Werktage zu festen Uhrzeiten, belegt ist, was schon gebucht ist)
export const SLOT_ZEIT = ['09:00', '10:00', '11:00', '14:00', '15:00'] as const;

// Status je Seite (Leon 2026-10-21, Durchsicht 3): Namen verbindlich
export const ST_C = {
  erst: ['Erstgespräch', 'neutral'], weiter: ['Weiterer Termin – Closer', 'accent'], spaeter: ['Später melden (vor Beratergespräch)', 'warn'],
  berater: ['Beratergespräch gebucht', 'ok'], vertrag: ['Beratergespräch gebucht', 'ok'], abgeschlossen: ['Abgeschlossen', 'ok'], kein: ['Kein Interesse mehr', 'neutral'], nie: ['Erstgespräch nie stattgefunden', 'neutral']
} as const;
export const ST_B = {
  erst: ['Berater-Erstgespräch', 'neutral'], weiter: ['Weiterer Termin – Berater', 'accent'], spaeter: ['Später melden (nach Beratergespräch)', 'warn'],
  vertrag: ['Vertragsphase', 'ok'], abgeschlossen: ['Abgeschlossen', 'ok'], kein: ['Kein Interesse mehr', 'neutral']
} as const;
/** Farbe je Phase (Leon 2026-10-30): Rot und Orange bleiben den Hinweisen (überfällig, Auswertung offen, nicht erschienen) */
const PHASE: Record<string, string> = {
  'Erstgespräch': 'ph-erst', 'Weiterer Termin – Closer': 'ph-weiter', 'Später melden (vor Beratergespräch)': 'ph-spaeter', 'Beratergespräch gebucht': 'ph-berater',
  'Berater-Erstgespräch': 'ph-berater', 'Weiterer Termin – Berater': 'ph-weiter', 'Später melden (nach Beratergespräch)': 'ph-spaeter-b', 'Vertragsphase': 'ph-vertrag',
  'Abgeschlossen': 'ph-ab', 'Kein Interesse mehr': 'ph-aus', 'Erstgespräch nie stattgefunden': 'ph-aus'
};
export const phaseKlasse = (status: string) => PHASE[status] ?? 'neutral';
export type StatusC = keyof typeof ST_C;
export type StatusB = keyof typeof ST_B;
export const STATUS_C = Object.keys(ST_C) as StatusC[];
export const STATUS_B = Object.keys(ST_B) as StatusB[];
export const OFFEN_ST = ['erst', 'weiter', 'spaeter'] as const;

/** Status eines Kunden aus Sicht einer Seite; vom Closer selbst ausgelöste Vertragsphase heißt auch bei ihm „Vertragsphase“. */
export function statusVon(k: { status_c: string; status_b: string | null; vertrag_status: string | null; berater_id: number | null }, seite: 'c' | 'b'): [string, string] {
  if (seite === 'b' && k.status_b) return [...ST_B[k.status_b as StatusB]];
  if (k.status_c === 'vertrag' && !k.status_b && k.vertrag_status) return ['Vertragsphase', 'ok'];
  return [...(ST_C[k.status_c as StatusC] ?? ['–', 'neutral'])];
}

export const kundenId = (nr: number) => '1-' + String(nr).padStart(3, '0');
export const idAusKundenId = (kid: string): number | null => { const m = /^1-(\d{3,})$/.exec(kid); return m ? Number(m[1]) : null; };

/** Mail in die neueste Nachricht und den zitierten Verlauf darunter teilen (wie Gmail „ältere Nachrichten“; Leon 2026-10-31). */
export function mailTeilen(roh: string): [string, string] {
  // Outlook schickt Windows-Zeilenenden und trennt den Verlauf mit „Von: … / Gesendet: …“ oder einer Unterstrich-Linie
  const text = roh.replace(/\r\n?/g, '\n');
  const m = /^(?:(?:Am|On) .{4,200}(?:schrieb|wrote)[^\n]*:?|-{2,} ?(?:Original|Ursprüngliche|Forwarded) ?(?:Nachricht|Message)[^\n]*|_{10,}|(?:Von|From): .+\n(?:(?:Gesendet|Sent|Datum|Date): .+))\s*$|^>/im.exec(text);
  if (!m || m.index < 1) return [text, ''];
  return [text.slice(0, m.index).trimEnd(), text.slice(m.index).trim()];
}

/* ---------- Verlauf: Art aus dem Text (Prototyp VTYP) ---------- */
export type VerlaufArt = 'notiz' | 'wv' | 'info' | 'vertrag' | 'termin';
export function verlaufArt(t: string): [string, VerlaufArt] {
  if (/^Info:/.test(t)) return ['Info', 'info'];
  if (/^Notiz/.test(t)) return ['Notiz', 'notiz'];
  if (/^Wiedervorlage/.test(t)) return ['Wiedervorlage', 'wv'];
  if (/^Anruf|^Unterlagen|^Kaltmail|selbst gebucht|^Nachfassen/.test(t)) return ['', 'info'];
  if (/^Vertrag|^Abgeschlossen|^BAFA|^Förderanfrage|^Überführung|Vertragsphase ausgelöst/.test(t)) return ['Vertrag', 'vertrag'];
  if (/stattgefunden|erschienen|abgesagt|verschoben|·/.test(t)) return ['Termin', 'termin'];
  return ['Info', 'info'];
}

/* ---------- Aufgaben: Dringlichkeit und Sortierung (Leon 2026-10-21: überfällig zuerst, das älteste oben) ---------- */
export type Dringlichkeit = 'ueber' | 'heute' | 'spaeter';
export const DRING_TON: Record<Dringlichkeit, string> = { ueber: 'crit', heute: 'warn', spaeter: 'neutral' };
export const dringlichkeit = (faellig: string, heute: string): Dringlichkeit => faellig < heute ? 'ueber' : faellig === heute ? 'heute' : 'spaeter';

export interface Aufgabe {
  id: string; art: string; firma: string; kontext: string; faellig: string; seit: string; seitTage: number; dring: Dringlichkeit;
  // Wohin ein Klick führt: Arbeitsmaske (Anrufzeile), Kundenansicht, Chat
  ziel: { art: 'anruf'; zeile: number; kampagne: number } | { art: 'kunde'; kid: string } | { art: 'chat'; chat: number } | { art: 'nachhaken'; kid: string }
    // Buchhaltung: Rechnung öffnen oder zu einer Seite gehen (Prototyp data-brid, data-bgo)
    | { art: 'bh'; rid?: number; go?: string };
  wer?: string;
  // Datei zur Aufgabe (z. B. der unterschriebene Vertrag in „2 Für dich zu unterschreiben“)
  link?: string;
  // Wiedervorlage: an wen sie weitergegeben werden kann (Leon 2026-10-28)
  weiter?: { id: number; name: string }[];
}
const DR: Dringlichkeit[] = ['ueber', 'heute', 'spaeter'];
export const aufgabenSort = (a: Aufgabe, b: Aufgabe) => DR.indexOf(a.dring) - DR.indexOf(b.dring) || a.faellig.localeCompare(b.faellig) || b.seitTage - a.seitTage;

/** „überfällig seit Mo 19.10.“, „fällig heute“, „fällig Fr 23.10.“ */
export const dringText = (a: Pick<Aufgabe, 'dring' | 'faellig'>, tagText: (iso: string) => string) =>
  a.dring === 'ueber' ? `überfällig seit ${tagText(a.faellig)}` : a.dring === 'heute' ? 'fällig heute' : `fällig ${tagText(a.faellig)}`;

/* ---------- Datum ---------- */
const WT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
/** „Mi 21.10.“ aus JJJJ-MM-TT */
export const tagText = (iso: string) => { const d = new Date(iso + 'T12:00:00Z'); return `${WT[d.getUTCDay()]} ${iso.slice(8, 10)}.${iso.slice(5, 7)}.`; };
export const plusTageIso = (iso: string, n: number) => { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
/** Fünf Werktage der Woche ab dem Montag nach `iso` plus w Wochen */
export function wocheAb(iso: string, w: number): string[] {
  const d = new Date(iso + 'T12:00:00Z');
  const bisMontag = (8 - d.getUTCDay()) % 7 || 7;
  const mo = plusTageIso(iso, bisMontag + 7 * w);
  return [0, 1, 2, 3, 4].map(i => plusTageIso(mo, i));
}
/** Montag der Woche von `iso` plus w Wochen, fünf Werktage */
export function dieseWoche(iso: string, w: number): string[] {
  const d = new Date(iso + 'T12:00:00Z');
  const mo = plusTageIso(iso, -((d.getUTCDay() + 6) % 7) + 7 * w);
  return [0, 1, 2, 3, 4].map(i => plusTageIso(mo, i));
}

/* ---------- Vergütung ---------- */
export const ANLAESSE = ['Termin stattgefunden', 'Termin qualifiziert', 'Abschluss', 'Fixum'] as const;
export const REGEL_ARTEN: Record<(typeof ANLAESSE)[number], string[]> = {
  'Termin stattgefunden': ['Festbetrag je Termin', 'Stundensatz nach Dauer'], 'Termin qualifiziert': ['Festbetrag je Termin'],
  'Abschluss': ['Festbetrag je Kunde', 'Prozent vom Nettoumsatz'], 'Fixum': ['Festbetrag je Monat']
};
export const TERMINE_F = ['alle Termine', 'nur Erstgespräche', 'nur weitere Termine'] as const;
/** Betrag nach Minuten bei Stundensatz, auf Cent gerundet (200 € je Stunde, 64 Min. = 213,33 €). */
export const betragNachMinuten = (min: number, satz: number) => Math.round(min * satz / 60 * 100) / 100;
export const euroText = (n: number) => `${n.toLocaleString('de-DE', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })} €`;

/* ---------- Vertrag aus Vorlage (neuratek/software/crm/vertragsphase.md) ---------- */

/** <<Frist>> = Zahlwort und Ziffer, z. B. „acht (8)“ */
export const zahlWort = (n: number) => ['null', 'eins', 'zwei', 'drei', 'vier', 'fünf', 'sechs', 'sieben', 'acht', 'neun', 'zehn', 'elf', 'zwölf', 'dreizehn', 'vierzehn', 'fünfzehn', 'sechzehn'][n] ?? String(n);
/** „3.500“, „3500,5“ → Zahl */
export const nettoAusText = (s: string) => Number(String(s).replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '')) || 0;
const betrag = (n: number) => `${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const glatt = (s: string) => s.replace(/\s+/g, ' ').trim();

export interface VertragAngaben { firma: string; adresse: string; partner: string; netto: number; quote: number | null; bereich: string; frist: number }
/**
 * Werte für die Platzhalter: jeder Wert ohne Leerzeichen davor oder danach, einzige Ausnahme <<Bereich>> („ im Bereich …“ oder leer);
 * Beträge „3.500,00 €“, Förderquote „80 %“, Vertragspartner nur der Name (ohne „Herr“, ohne Zusatz in Klammern).
 * <<Ort, Datum>> füllt die Software nicht mehr; Leon hat den Platzhalter aus den Vorlagen genommen (2026-10-31).
 */
export function vertragPlatzhalter(a: VertragAngaben): Record<string, string> {
  const w: Record<string, string> = {
    Firma: glatt(a.firma), Adresse: glatt(a.adresse), Vertragspartner: glatt(a.partner).replace(/^(herrn?|frau)\s+/i, '').replace(/\s*\([^)]*\)$/, ''),
    'Vergütung': betrag(a.netto), Frist: `${zahlWort(a.frist)} (${a.frist})`, Bereich: glatt(a.bereich) ? ` im Bereich ${glatt(a.bereich)}` : ''
  };
  if (a.quote) { w['Förderquote'] = `${a.quote} %`; w['Förderbetrag'] = betrag(Math.round(a.netto * a.quote) / 100); }
  return w;
}
/** Name der Kopie: „Dienstleistungsvertrag – Leistung – Firma“ (Konzeptentwicklung mit dem reinen Bereich hinter der Leistung) */
export const vertragTitel = (leistung: string, bereich: string, firma: string) =>
  `Dienstleistungsvertrag – ${[glatt(leistung.split(':')[0]), glatt(bereich)].filter(Boolean).join(' ')} – ${glatt(firma)}`.replace(/[/\\]/g, '-');

export interface RegelKern { anlass: string; art: string; betrag: number; termine: string; leistung: string; ab: string; bis: string | null; rolle: string }
/** Die Regeln, die an einem Tag für einen Anlass gelten (gültig ab, bis), passend zu Termin-Art und Leistung. */
export function passendeRegeln(regeln: RegelKern[], p: { anlass: string; datum: string; rolle: string; erst?: boolean; leistung?: string }): RegelKern[] {
  return regeln.filter(r => r.anlass === p.anlass && r.rolle === p.rolle && r.ab <= p.datum && (!r.bis || r.bis >= p.datum)
    && (r.termine === 'alle Termine' || r.termine === '' || (r.termine === 'nur Erstgespräche' ? p.erst === true : p.erst === false))
    && (r.leistung === 'alle' || !p.leistung || p.leistung.startsWith(r.leistung)));
}
export function regelText(r: Pick<RegelKern, 'art' | 'betrag'>): string {
  if (r.art.startsWith('Prozent')) return `${String(r.betrag).replace('.', ',')} % vom Nettoumsatz`;
  if (r.art.startsWith('Stundensatz')) return `${euroText(r.betrag)} je Stunde, nach Minuten`;
  return `${euroText(r.betrag)} ${r.art.replace('Festbetrag ', '')}`;
}

/** Platzhalter einer CRM-Vorlage füllen ({Ansprechpartner}, {Firma}, {Förderung}). */
export const vorlageFuellen = (t: string, w: { ap: string; firma: string; quote: string }) =>
  t.replace(/\{Ansprechpartner\}/g, w.ap).replace(' ,', ',').replace(/\{Firma\}/g, w.firma).replace(/\{Förderung\}/g, `bis zu ${w.quote}`);

export const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'] as const;
